
import { makeSingleClickOnlyHandler } from '@@/bits/click'
import { isAppUsing } from '@@/bits/flip'
import { formatScheduledDateAndTime } from '@@/bits/post_schedule'
import { isPostPinned, isPostScheduled } from '@@/bits/post_state'
import { getDateStringForDisplay } from '@@/bits/time'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { BreakPoints } from '@@/pinia/window_size'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import type { BeethovenLuminance } from '@padlet/beethoven-client/lib/types'
import appCan from 'bits/app_can'
import { CONFIRM_DELETE_POST, CONFIRM_MODERATION_REJECT_POST } from 'bits/confirmation_dialog'
import { tailwindSurfaceFontClass } from 'bits/font'
import overflowBorderRadiusTransformFixStyle from 'bits/overflow_border_radius_transform_fix'
import { PiniaMapAction, PiniaMapState } from 'bits/pinia'
import { postApprovalColorTailwindClass, postColorTailwindClass } from 'bits/post_color'
import { observeSizeChange } from 'bits/resize_observer'
import { isUrl } from 'bits/url'
import { Action, Component, Getter, Prop, State, Vue, Watch, namespace } from 'bits/vue'
import { debounce } from 'lodash-es'
import { useSurfaceStore } from 'pinia/surface'
import { useReactionsStore } from 'pinia/surface_reactions'
import type { Cid, Comment, Id, Post, PostConnection, User, WallReactionData, WallViz } from 'types'
import SurfacePostAttachment from 'vuecomponents/SurfacePostAttachment.vue'
import SurfacePostAttachmentEmbedControl from 'vuecomponents/SurfacePostAttachmentEmbedControl.vue'
import SurfacePostAuthor from 'vuecomponents/SurfacePostAuthor.vue'
import SurfacePostBody from 'vuecomponents/SurfacePostBody.vue'
import SurfacePostComments from 'vuecomponents/SurfacePostComments.vue'
import SurfacePostModeration from 'vuecomponents/SurfacePostModeration.vue'
import SurfacePostMoreActions from 'vuecomponents/SurfacePostMoreActions.vue'
import SurfacePostPoll from 'vuecomponents/SurfacePostPoll.vue'
import SurfacePostSocialContentTopBar from 'vuecomponents/SurfacePostSocialContentTopBar.vue'
const FIXED_POST_WIDTH: Partial<Record<WallViz, number>> = {
  timeline: 256,
  table: 300,
}

@Component({
  components: {
    SurfacePostAuthor,
    SurfacePostBody,
    SurfacePostAttachment,
    SurfacePostModeration,
    SurfacePostSocialContentTopBar,
    SurfacePostComments,
    SurfacePostMoreActions,
    SurfacePostAttachmentEmbedControl,
    SurfacePostPoll,
    OzIcon,
  },
})
export default class SurfacePostBeingShowed extends Vue {
  $el!: HTMLDivElement
  $refs!: {
    postBanner: HTMLDivElement
    beforeAttachment: HTMLDivElement
    userContent: HTMLDivElement
    postContent: HTMLDivElement
    attachment: SurfacePostAttachment
  }

  @Prop() index!: number
  @Prop() post!: Post
  @Prop() isLast!: boolean
  @Prop() fitHeightToContainer!: boolean
  @Prop() verticallyCenterContent!: boolean
  @Prop({ default: () => [] }) postReactionIds
  @Prop({ default: () => [] }) postCommentIds

  // **** GENERAL SHOW ATTRIBUTES ****
  @Getter colorScheme!: 'light' | 'dark'
  @Getter xAuthor!: boolean
  @PiniaMapState(useSurfaceStore) xCommentsUnderPosts!: boolean
  @PiniaMapState(useSurfaceStore) isPostSizeWide!: boolean
  @Getter isStream!: boolean
  @Getter isCanvas!: boolean
  @Getter isShelf!: boolean
  @Getter isMap!: boolean
  @Getter isTimeline!: boolean
  @Getter fontId!: number
  @Getter dir!: 'ltr' | 'rtl' | 'auto'
  @Getter isApp!: boolean
  @namespace('browser').Getter isSmallerThanDesktop!: boolean

  @State isSafari!: boolean

  get isPadletColorSchemeDark(): boolean {
    return this.colorScheme === 'dark'
  }

  get postColorClass(): string {
    return postColorTailwindClass({ postColor: this.post.color, isLightColorScheme: !this.isPadletColorSchemeDark })
  }

  get overflowBorderRadiusTransformFixStyle(): Record<string, string> {
    return overflowBorderRadiusTransformFixStyle(this.isSafari)
  }

  get isAwaitingApproval(): boolean {
    return this.isPostNotApproved && !this.canIModerate
  }

  get isScheduled(): boolean {
    return isPostScheduled(this.post)
  }

  get xPin(): boolean {
    return isAppUsing('pinPosts') && isPostPinned(this.post)
  }

  get xPostBanner(): boolean {
    return this.isAwaitingApproval || this.isScheduled || this.xPin
  }

  @namespace('postConnection').State postToConnectFromId!: Id
  @namespace('postConnection').Getter connections!: PostConnection[]

  areConnected(post1Id, post2Id) {
    return (
      this.connections.filter((c) => {
        return (
          (c.from_wish_id === post1Id && c.to_wish_id === post2Id) ||
          (c.from_wish_id === post2Id && c.to_wish_id === post1Id)
        )
      }).length > 0
    )
  }

  get xConnect(): boolean {
    return (
      !!this.postToConnectFromId &&
      this.postToConnectFromId !== this.post.id &&
      !this.areConnected(this.post.id, this.postToConnectFromId)
    )
  }

  get scheduledAtDateString(): string | undefined {
    if (this.post.scheduled_at == null) return undefined
    return getDateStringForDisplay(this.post.scheduled_at)
  }

  get formattedScheduledDateAndTime(): { date: string; time: string } {
    return formatScheduledDateAndTime(this.post.scheduled_at!)
  }

  get subject(): string | undefined {
    return this.post.subject
  }

  get xSubject(): boolean {
    return !!this.subject
  }

  get body(): string | undefined {
    return this.post.body
  }

  get tailwindFontClass(): string {
    return tailwindSurfaceFontClass(this.fontId)
  }

  get xBody(): boolean {
    return !!this.body
  }

  /**
   * If the more button is on top of attachment, we make it overlay the attachment
   * Otherwise, we make the post text avoid the more button with a div with float: right
   */
  get xInvisibleFloatingDiv(): boolean {
    if (!this.xAuthor && this.xSubject) return true
    if (!this.xAuthor && !this.xSubject && !this.xAttachment && !this.xPoll) return true
    return false
  }

  get xOverlayForMoreButtons(): boolean {
    if (this.xAuthor) return false
    return !this.xInvisibleFloatingDiv
  }

  /**
   * Only for timeline: the whole post can be scrolled.
   */
  get isWishContentScrollable(): boolean {
    return this.isTimeline || this.isMap
  }

  /**
   * Only load attachment when there is one and when post is in viewport
   */
  get xAttachment(): boolean {
    const attachment = this.post.attachment
    const hasAttachment = !!attachment && isUrl(attachment)
    return hasAttachment
  }

  get xPoll(): boolean {
    return this.post?.wish_content?.attachment_props?.type === 'poll'
  }

  get poll() {
    return this.xPoll ? this.post?.wish_content?.attachment_props : null
  }

  // STALKER BAR

  get xSocialContent(): boolean {
    return !this.isPostNotApproved && (this.xComments || this.xReactions)
  }

  // TODO: decouple these reactions and comments logic from SurfacePostBeingShowed
  // REACTIONS

  @Getter isReactable!: boolean
  @Getter reactionData!: WallReactionData
  @PiniaMapState(useReactionsStore) accumulatedReactionsByWishId
  @namespace('browser').Getter isPhone!: boolean

  get xReactions(): boolean {
    // when a user is viewing a wall with reaction disabled and then reaction is enabled + someone react to something
    // the realtime message for new reaction may come before the reaction enable message
    // => wait for reactionData to be available before showing reactions
    const reactionCount =
      this.post.id != null ? this.accumulatedReactionsByWishId[this.post.id]?.totalReactionsCount : 0
    return (this.isReactable || this.postReactionIds.length > 0 || reactionCount > 0) && !!this.reactionData
  }

  // COMMENTS

  @Getter isCommentable!: boolean
  @namespace('comment').State commentBeingEditedId!: Id
  @namespace('comment').State('commentEntities') wallComments!: Comment[]

  get xComments(): boolean {
    // Turn comments off when switching to Backchannel
    return this.post.published === true && (this.isCommentable || this.postCommentIds.length > 0)
  }

  // ATTACHMENT

  @namespace('attachment').Getter linkDisplayAttributesForUrl!: (attachment: string) => BeethovenDisplayAttributes

  xEmbedControl = false
  embedData: {
    downloadUrl?: string
    isDownloadable?: boolean
  } = {}

  embedControlPositionStyle = {}
  embedControlRepositionObserver: any | null = null

  get attachmentAttributes(): BeethovenDisplayAttributes | null {
    if (!this.post.attachment) return null
    return this.linkDisplayAttributesForUrl(this.post.attachment)
  }

  get attachmentLuminance(): BeethovenLuminance | null {
    return (this.attachmentAttributes && this.attachmentAttributes.image_luminance) || null
  }

  get shouldSyncEmbedControlSize(): boolean {
    return this.xEmbedControl && !this.isWishContentScrollable && !!this.post.attachment
  }

  @Watch('shouldSyncEmbedControlSize')
  toggleEmbedControlObserver(newValue, _oldVlaue): void {
    if (newValue) {
      const observer = observeSizeChange(this.$refs.postContent, this.calculateEmbedControlPosition)
      if (!observer) return
      observer.observe(this.$refs.beforeAttachment)
      this.$refs.postBanner && observer.observe(this.$refs.postBanner)
      this.embedControlRepositionObserver = observer
    } else {
      this.embedControlRepositionObserver?.disconnect()
      this.embedControlRepositionObserver = null
    }
  }

  /**
   * When attachment has an inline embed (e.g. Youtube, Spotify), SurfacePostAttachment will handle rendering the embed inline
   * while the embed control will be rendered here.
   * We need to calculate embed control position and recalculate if any post content is resized with ResizeObserver.
   */
  toggleEmbedControl({ xEmbed, downloadUrl, isDownloadable }): void {
    this.embedData = { downloadUrl, isDownloadable }
    this.xEmbedControl = xEmbed
  }

  postMessageToAttachment(event: string): void {
    this.$refs.attachment?.postMessage(event)
  }

  expand(post: Post): void {
    this.postMessageToAttachment('back-to-preview')
    this.expandPost({ postCid: post.cid, xTextPanel: !this.isSmallerThanDesktop })
  }

  calculateEmbedControlPosition(): void {
    this.$nextTick(() => {
      if (!this.shouldSyncEmbedControlSize) return

      const embedWrapperRef = this.$refs.attachment?.$refs?.embedWrapper
      if (!embedWrapperRef) return

      const DISTANCE_TO_EMBED = 8
      const embedHeight = embedWrapperRef.offsetHeight
      const margins =
        DISTANCE_TO_EMBED +
        parseInt(window.getComputedStyle(this.$refs.userContent).paddingTop, 10) +
        parseInt(window.getComputedStyle(this.$refs.attachment.$el).marginTop, 10)
      const awaitingApprovalHeight = this.$refs.postBanner?.offsetHeight || 0
      const beforeAttachmentHeight = this.$refs.beforeAttachment.offsetHeight
      const postContentHeight = this.$refs.postContent.offsetHeight
      this.embedControlPositionStyle = {
        top: -(postContentHeight - embedHeight - beforeAttachmentHeight - awaitingApprovalHeight - margins) + 'px',
      }
    })
  }

  // **** PERMISSION-RELATED SHOW ATTRIBUTES ****

  @State user!: User
  @Getter canIModerate!: boolean
  @Getter canIWrite!: boolean

  get wasWrittenByCurrentUser(): boolean {
    return !!this.user.id && this.post.author_id === this.user.id
  }

  get isEditableByCurrentUser(): boolean {
    return this.canIModerate || (this.wasWrittenByCurrentUser && this.canIWrite)
  }

  // **** INTERACTIONS ****

  // Post
  @namespace('postAction').Action expandPost!: (payload: {
    postCid: Cid
    xTextPanel?: boolean
    shouldFocusInput?: boolean
  }) => void

  @namespace('postAction').Action showPostActionMenu!: (payload: { postCid: Cid }) => void
  @namespace('postAction').Action setPostRightClickCoordinates!: (payload: {
    postRightClickCoordinates: [number, number]
  }) => void

  @namespace('post').Action startEditingPost!: (payload: { postCid: Cid }) => void
  @namespace('post').Action deletePost!: (payload: { postCid: Cid }) => void

  @State isMobile!: boolean

  tryShowActions(e: MouseEvent): void {
    if (
      this.isMobile ||
      this.xConnect ||
      ['a', 'textarea', 'input', 'select', 'option'].includes((e.target as Element).tagName?.toLowerCase() || '') ||
      e.shiftKey
    ) {
      return
    }
    e.preventDefault()
    this.setPostRightClickCoordinates({ postRightClickCoordinates: [e.pageX, e.pageY] })
    this.showPostActionMenu({ postCid: this.post.cid })
  }

  tryStartEditPost(): void {
    if (this.isEditableByCurrentUser) {
      this.startEditingPost({ postCid: this.post.cid })
    }
  }

  @namespace('confirmationDialog').Action openConfirmationDialog!: (params: unknown) => void

  // Comment
  @namespace('comment').Action createComment!: (...args: any[]) => void
  @namespace('comment').Action editComment!: (...args: any[]) => void
  @namespace('comment').Action stopEditingComment!: (...args: any[]) => void
  @namespace('comment').Action showCommentActionMenu!: (...args: any[]) => void
  @Action showCommentsPanel!: (payload) => void

  @PiniaMapAction(useCommentsStore) createCommentV2!: ({ postId, htmlBody }: { postId: Id; htmlBody: string }) => void
  @PiniaMapAction(useCommentsStore) editCommentV2!: ({ id, htmlBody }: { id: Id; htmlBody: string }) => void

  showComments(): void {
    if (!appCan('useNativeCommentsPanel')) {
      this.expandPost({ postCid: this.post.cid, xTextPanel: true, shouldFocusInput: !this.isPhone })
    } else {
      // native app should still show the old comments panel
      this.showCommentsPanel({ postId: this.post.id })
    }
  }

  // **** POST APPROVAL ****

  @namespace('post').Action approvePost!: (payload: { postCid: Cid }) => void
  @PiniaMapAction(useSurfacePostsStore) rejectPost!: (id?: Id) => Promise<void>

  get isPostNotApproved(): boolean {
    return !this.post.published
  }

  get postApprovalBgColorClass(): string {
    return postApprovalColorTailwindClass({
      post: this.post,
      isLightColorScheme: !this.isPadletColorSchemeDark,
    })
  }

  confirmDelete(): void {
    // just delete new/empty posts
    if (!this.post.id || (!this.post.body && !this.post.subject && !this.post.attachment)) {
      this.deletePost({ postCid: this.post.cid })
    }

    this.openConfirmationDialog({
      ...CONFIRM_DELETE_POST,
      afterConfirmActions: [{ type: 'post/deletePost', payload: { postCid: this.post.cid } }],
      xShadow: true,
    })
  }

  confirmModerationReject(): void {
    this.openConfirmationDialog({
      ...CONFIRM_MODERATION_REJECT_POST,
      afterConfirmActions: [() => this.rejectPost(this.post.id)],
      xShadow: true,
    })
  }

  // KEEP TRACK OF ITS OWN CLIENT WIDTH

  @Getter format!: string
  @namespace('browser').Getter windowWidth!: number

  clientWidth = 0
  resizeObserver: ResizeObserver | null = null

  observeResize(): void {
    this.resizeObserver = observeSizeChange(
      this.$el,
      debounce((_, rect: DOMRectReadOnly) => {
        this.clientWidth = rect.width
      }, 100),
    )
  }

  unobserveResize(): void {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
  }

  get displayedPostWidth(): number {
    const variableWidth = Math.ceil(this.clientWidth)
    switch (this.format) {
      case 'stream': {
        const MAX_STREAM_POST_WIDTH = this.isPostSizeWide ? 748 : 540
        // See SurfacePostsStream.vue
        if (this.windowWidth <= BreakPoints.TabletPortrait) {
          return variableWidth
        }
        return MAX_STREAM_POST_WIDTH
      }
      case 'grid': {
        return variableWidth
      }
      case 'matrix': {
        return variableWidth
      }
      case 'free': {
        return this.post.width || 256
      }
      case 'timeline_v2': {
        const MAX_TIMELINE_V2_POST_WIDTH = this.isPostSizeWide ? 360 : 256
        return MAX_TIMELINE_V2_POST_WIDTH
      }
      case 'map': {
        const MAX_MAP_POST_WIDTH = this.isPostSizeWide ? 380 : 256
        return MAX_MAP_POST_WIDTH
      }
      default: {
        return FIXED_POST_WIDTH[this.format] ?? (Math.ceil(this.post.width || 0) || 1920)
      }
    }
  }

  saveEditComment({ id, body }: { id: Id; body: string }): void {
    if (isAppUsing('surfaceCommentsV2')) {
      this.editCommentV2({ id, htmlBody: body })
      return
    }
    this.editComment({ id, body })
  }

  publishComment({ postId, body }: { postId: Id; body: string }): void {
    if (isAppUsing('surfaceCommentsV2')) {
      this.createCommentV2({
        postId,
        htmlBody: body,
      })
      return
    }
    this.createComment({
      postId,
      body,
    })
  }

  // COMPONENT LIFECYCLE

  expandPostOnSingleClick = (_: MouseEvent): void => {}

  mounted(): void {
    this.expandPostOnSingleClick = makeSingleClickOnlyHandler(
      () => {
        this.expand(this.post)
      },
      {
        skipIf: (e) => {
          // Prevent expanding post when it's being dragged
          return (
            (e.target instanceof HTMLElement && e.target.closest('.surface-post')?.classList?.contains('noclick')) ??
            false
          )
        },
      },
    )

    this.clientWidth = this.$el.offsetWidth
    // If posts in this format have fixed width, no need to observe resize
    if (!(this.format in FIXED_POST_WIDTH)) {
      this.observeResize()
    }
  }

  beforeDestroy(): void {
    this.unobserveResize()
  }
}
